<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import {
		getDeviceStatus,
		getDeviceSettings,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { beforeUpdate } from 'svelte';
	import ButtonType from './ButtonType.svelte';
	import ButtonModeOptions from './ButtonModeOptions.svelte';
	import InitialStateOptions from './InitialStateOptions.svelte';
	import ButtonTypeInputsReader from './ButtonTypeInputsReader.svelte';
	import IoModeAndPower from './IoModeAndPower.svelte';
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
	} from '../../../../../helpers/interface_helpers';
	import { gen2Request } from '../../../../../services/gen_2_http_reqs';
	import type { SettingRequest } from '../../../../../types/requests/settings/setting';
	import { Button, Container, toast } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { DEVICES_WITH_CYCLE_INPUT_MODE } from 'src/constants';
	import { compare2Versions } from 'src/helpers/firmware_compare_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import SettingGuide from '../../../SettingGuide.svelte';
	import EnableInputG2 from './EnableInputG2.svelte';
	import InvertSwitchG2 from './InvertSwitchG2.svelte';
	import LockInputsG2 from './LockInputsG2.svelte';
	import PhysicalInputContainer from './PhysicalInputContainer.svelte';
	import InputState from '../../../ApplicationSettings/InputState.svelte';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { deviceSettingsAdvanced } from 'src/helpers/settings/device_settings_advanced';
	import {
		buildIoFieldConfig,
		deviceHasInputStateSetting,
		deviceShowsInputStateSetting,
		isNonEmptyConfig,
		sendIoSavePayloads,
	} from './io_save';
	import AdvancedSettingBlock from './AdvancedSettingBlock.svelte';
	import { devices } from 'src/stores/lists/devices';

	export let device;

	const id = real_id(device.id);

	$: settingsStore = getDeviceSettings(id);
	let settings = readDeviceSettings(id);
	$: settings = $settingsStore ?? settings;
	let typeValue = settings[`input:${device.channel}`].type;
	let cacheTypeValue = typeValue;
	let modeValue =
		device.category !== 'inputs_reader'
			? settings[`${get_gen_2_component_channel_structure(device)}`].in_mode
			: '';
	let cacheModeValue = modeValue;
	let initialStateValue =
		device.category !== 'inputs_reader'
			? settings[`${get_gen_2_component_channel_structure(device)}`].initial_state
			: '';
	let cacheInitialStateValue = initialStateValue;
	$: reactiveStateValue = initialStateValue;

	const firmWareVersion = settings?.sys?.device.fw_id || settings?.DeviceInfo?.fw_id;
	const mandatoryUpdate = compare2Versions(firmWareVersion, '1.3.3') === 1;
	$: inputSettings = settings[`input:${device.channel}`];
	$: controlSettings = settings[`${get_gen_2_component_channel_structure(device)}`];
	$: showEnableInput =
		inputSettings?.enable !== undefined && compare2Versions(firmWareVersion, '1.0.7') !== 1;
	$: showLockInputs =
		controlSettings?.in_locked !== undefined && compare2Versions(firmWareVersion, '1.6.0') !== 1;
	$: showInvertSwitch = inputSettings?.invert !== undefined;
	$: hasInputStateSetting = deviceHasInputStateSetting(device);
	$: showInputState = deviceShowsInputStateSetting(device, $deviceSettingsAdvanced);
	$: showPhysicalInput =
		$showNewLayout && (showEnableInput || showLockInputs || showInvertSwitch || showInputState);

	let enableValue = settings[`input:${device.channel}`]?.enable ?? false;
	let cacheEnable = enableValue;
	let lockValue = settings[`${get_gen_2_component_channel_structure(device)}`]?.in_locked ?? false;
	let cacheLock = lockValue;
	let invertValue = settings[`input:${device.channel}`]?.invert ?? false;
	let cacheInvert = invertValue;
	let inputStateValue = $devices[device.id]?.input_state || false;
	let cacheInputState = inputStateValue;
	const options = ['button', 'switch'];
	const _options = [
		$format('working_type_button', { default: 'Button' }),
		$format('working_type_switch', { default: 'Switch' }),
	];
	const buttonModeOptions = {
		follow: {
			type: 'switch',
			text: $format('btn_mode_follow_description', {
				default:
					'Set Shelly device to be "Toggle" switch. Act as a flip switch with one state for "ON" and one state for "OFF".',
			}),
			title: $format('btn_mode_follow_title', { default: 'Toggle switch' }),
			description: $format('btn_mode_follow_desc', {
				default: 'Act as a flip switch with one state for "ON" and one state for "OFF".',
			}),
		},
		momentary: {
			type: 'button',
			text: $format('btn_mode_momentary_description', {
				default: 'Set Shelly device to be "Momentary" switch. Push for "ON" or Push for "OFF".',
			}),
			title: $format('btn_mode_momentary_title', { default: 'Momentary switch' }),
			description: $format('btn_mode_momentary_desc', {
				default: 'Push for "ON" or push for "OFF".',
			}),
		},
		cycle: DEVICES_WITH_CYCLE_INPUT_MODE.includes(device?.type)
			? {
					type: 'button',
					text: $format('btn_mode_cycle_description', {
						default:
							'Set Shelly device to be "Cycle" switch. At the push of a input, the sequence is: Ch1-ON Ch2-OFF, Ch1-OFF Ch2-ON, Ch1 & Ch2 ON, Ch1 & Ch2 OFF',
					}),
					title: $format('btn_mode_cycle_title', { default: 'Cycle switch' }),
					description: $format('btn_mode_cycle_desc', {
						default:
							'At the push of an input, the sequence is: Ch1-ON Ch2-OFF, Ch1-OFF Ch2-ON, Ch1 & Ch2 ON, Ch1 & Ch2 OFF.',
					}),
				}
			: undefined,
		flip: {
			type: 'switch',
			text: $format('btn_mode_flip_description', {
				default: 'Set Shelly device to be "Edge" switch. Changes state on every hit.',
			}),
			title: $format('btn_mode_flip_title', { default: 'Edge switch' }),
			description: $format('btn_mode_flip_desc', {
				default: 'Changes state on every hit.',
			}),
		},
		detached: {
			type: 'any',
			text: $format('btn_mode_detached_description', {
				default:
					'Set Shelly device to be in "Detached" switch mode - switch is separated from the relays.',
			}),
			title: $format('btn_mode_detached_title', { default: 'Detached switch' }),
			description: $format('btn_mode_detached_desc', {
				default: 'Switch is separated from the relays.',
			}),
		},
		activate: {
			type: 'any',
			text: $format('btn_mode_activate_description', {
				default:
					'Activation switch - Used with motion sensor. Any input turns ON and resets Auto Off timer',
			}),
			title: $format('btn_mode_activate_title', { default: 'Activation switch' }),
			description: $format('btn_mode_activate_desc', {
				default: 'Used with motion sensor. Any input turns ON and resets Auto Off timer.',
			}),
		},
	};
	const initialStateOptions = {
		on: {
			type: 'any',
			text: $format('init_state_on_description', {
				default: 'Configure Shelly device to Turn ON, when it has power.',
			}),
			title: $format('init_state_on_title', { default: 'Turn on' }),
			description: $format('init_state_on_desc', {
				default: 'Automatically turns the device ON when power is restored.',
			}),
		},
		off: {
			type: 'any',
			text: $format('init_state_off_description', {
				default: 'Configure Shelly device to Turn OFF, when it has power.',
			}),
			title: $format('init_state_off_title', { default: 'Turn off' }),
			description: $format('init_state_off_desc', {
				default: 'Keeps the device OFF when power is restored.',
			}),
		},
		restore_last: {
			type: 'any',
			text: $format('init_state_report_last_description', {
				default:
					'Configure Shelly device to Restore the last mode it was in, when it has power.',
			}),
			title: $format('init_state_restore_last_title', { default: 'Restore last state' }),
			description: $format('init_state_restore_last_desc', {
				default: 'Returns the device to its previous state when power is restored.',
			}),
		},
		match_input: {
			type: 'switch',
			text: $format('init_state_match_input_description', {
				default:
					'Operates the Shelly device according to the state of the switch or button connected to the device.',
			}),
			title: $format('init_state_match_input_title', { default: 'Match input' }),
			description: $format('init_state_match_input_desc', {
				default:
					'Operates the device according to the state of the switch or button connected to the device.',
			}),
		},
	};

	let loading = false;

	const setTypeValue = async () => {
		const res_input_mode: SettingRequest = await gen2Request(device, 'Input.SetConfig', {
			id: device.channel,
			config: { type: typeValue },
		});

		if (res_input_mode.isok) {
			toast.open({
				type: 'success',
				title: $format('input_type_scc_toast', {
					values: { mode_value: _options[options.indexOf(typeValue)] },
					default: 'Button type set to {mode_value}.',
				}),
			});
			cacheTypeValue = typeValue;
		} else {
			typeValue = cacheTypeValue;
			toast.open({
				type: 'danger',
				title: $format('btn_type_err_toast', {
					default: 'Error! Could not change button type.',
				}),
			});
		}
	};

	beforeUpdate(() => {
		Object.keys(buttonModeOptions).forEach((key) => {
			if (typeof buttonModeOptions[key] === 'undefined') {
				delete buttonModeOptions[key];
			}
		});
	});

	const setModeAndInitialOptions = async () => {
		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			{
				id: device.channel,
				config: {
					in_mode: modeValue,
					initial_state: initialStateValue,
				},
			},
		);

		if (res.isok) {
			if (cacheModeValue !== modeValue) {
				toast.open({
					type: 'success',
					title: $format('btn_mode_scc_toast', {
						values: { mode_value: modeValue },
						default: 'Button mode set to {mode_value}.',
					}),
				});
				cacheModeValue = modeValue;
				settings[`${get_gen_2_component_channel_structure(device)}`].in_mode = modeValue;
				devices_settings.update_devices_settings(id, settings);
			}
			if (cacheInitialStateValue !== initialStateValue) {
				toast.open({
					type: 'success',
					title: $format('init_state_scc_toast', {
						values: { initial_state_value: $format(initialStateValue) },
						default: 'Initial state set to {initial_state_value}.',
					}),
				});
				cacheInitialStateValue = initialStateValue;
				settings[`${get_gen_2_component_channel_structure(device)}`].initial_state =
					initialStateValue;
				devices_settings.update_devices_settings(id, settings);
			}
		} else {
			if (cacheModeValue !== modeValue) {
				modeValue = cacheModeValue;
				toast.open({
					type: 'danger',
					title: $format('btn_mode_err_toast', {
						default: 'Error! Could not change button mode.',
					}),
				});
			}
			if (cacheInitialStateValue !== initialStateValue) {
				initialStateValue = cacheInitialStateValue;
				toast.open({
					type: 'danger',
					title: $format('init_state_err_toast', {
						default: 'Could not change input initial state.',
					}),
				});
			}
		}
	};
	const setButtonTypeInputs = async () => {
		const method = 'input.setconfig';
		const params = { id: device.channel, config: { type: typeValue } };

		const res: SettingRequest = await gen2Request(device, method, params);

		if (res.isok) {
			settings[`input:${device.channel}`].type = typeValue;
			devices_settings.update_devices_settings(id, settings);

			toast.open({
				type: 'success',
				title: $format('btn_type_scc_toast', {
					values: { type_value: typeValue },
					default: 'Button type set to {type_value}.',
				}),
			});
			cacheTypeValue = typeValue;
		} else {
			typeValue = cacheTypeValue;
			toast.open({
				type: 'danger',
				title: $format('btn_type_err_toast', {
					default: 'Error! Failed to set button type.',
				}),
			});
		}
	};

	const sentRequests = async () => {
		if (device.category !== 'inputs_reader') {
			loading = true;
			if (cacheTypeValue !== typeValue) {
				await setTypeValue();
			}
			if (cacheInitialStateValue !== initialStateValue || cacheModeValue !== modeValue) {
				await setModeAndInitialOptions();
			}

			loading = false;
		} else {
			loading = true;
			if (cacheTypeValue !== typeValue) {
				await setButtonTypeInputs();
			}
			loading = false;
		}
	};

	const saveAll = async () => {
		loading = true;
		const inputConfig = buildIoFieldConfig(
			{
				type: typeValue,
				...(showEnableInput ? { enable: enableValue } : {}),
				...(showInvertSwitch ? { invert: invertValue } : {}),
			},
			{
				type: cacheTypeValue,
				enable: cacheEnable,
				invert: cacheInvert,
			},
		);
		const controlConfig =
			device.category === 'inputs_reader'
				? {}
				: buildIoFieldConfig(
						{
							in_mode: modeValue,
							initial_state: initialStateValue,
							...(showLockInputs ? { in_locked: lockValue } : {}),
						},
						{
							in_mode: cacheModeValue,
							initial_state: cacheInitialStateValue,
							in_locked: cacheLock,
						},
					);

		const { ok } = await sendIoSavePayloads({
			device,
			inputUpdates: isNonEmptyConfig(inputConfig)
				? [{ id: device.channel, config: inputConfig }]
				: [],
			controlUpdates: isNonEmptyConfig(controlConfig)
				? [
						{
							id: device.channel,
							method: `${get_gen_2_component_from_category(device)}.SetConfig`,
							config: controlConfig,
						},
					]
				: [],
			inputState: showInputState ? inputStateValue : undefined,
			cachedInputState: cacheInputState,
		});

		if (ok) {
			if (inputConfig.type !== undefined) {
				settings[`input:${device.channel}`].type = typeValue;
				cacheTypeValue = typeValue;
			}
			if (inputConfig.enable !== undefined) {
				settings[`input:${device.channel}`].enable = enableValue;
				cacheEnable = enableValue;
			}
			if (inputConfig.invert !== undefined) {
				settings[`input:${device.channel}`].invert = invertValue;
				cacheInvert = invertValue;
			}
			if (controlConfig.in_mode !== undefined) {
				settings[`${get_gen_2_component_channel_structure(device)}`].in_mode = modeValue;
				cacheModeValue = modeValue;
			}
			if (controlConfig.initial_state !== undefined) {
				settings[`${get_gen_2_component_channel_structure(device)}`].initial_state =
					initialStateValue;
				cacheInitialStateValue = initialStateValue;
			}
			if (controlConfig.in_locked !== undefined) {
				settings[`${get_gen_2_component_channel_structure(device)}`].in_locked = lockValue;
				cacheLock = lockValue;
			}
			if (showInputState) {
				cacheInputState = inputStateValue;
			}
			devices_settings.update_devices_settings(id, settings);
		}
		loading = false;
	};

	$: isDirty =
		cacheTypeValue !== typeValue ||
		cacheModeValue !== modeValue ||
		cacheInitialStateValue !== initialStateValue ||
		(showEnableInput && enableValue !== cacheEnable) ||
		(showLockInputs && lockValue !== cacheLock) ||
		(showInvertSwitch && invertValue !== cacheInvert) ||
		(showInputState && inputStateValue !== cacheInputState);

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: saveAll,
				loading,
				disabled: loading || !isDirty,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	};
</script>

<SettingGuide
	text={$format('settings_guide_input_output', {
		default:
			'Choose how the wall switch or button drives the output — flip switch, momentary push, and what happens after a power cut.',
	})}
/>

{#if $showNewLayout}
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		{#if (device.category === 'module' && device.mode === 'inputs_reader') || device.category === 'inputs_reader'}
			<ButtonTypeInputsReader bind:typeValue />
		{:else}
			<ButtonType bind:typeValue {options} {_options} />
			<hr class="mb-m mt-s" />
			<IoModeAndPower>
				<ButtonModeOptions
					{typeValue}
					bind:modeValue
					{buttonModeOptions}
					_modeValue={modeValue}
					bind:reactiveStateValue
				/>
				<InitialStateOptions
					{typeValue}
					bind:initialStateValue
					{mandatoryUpdate}
					{initialStateOptions}
					{modeValue}
				/>
			</IoModeAndPower>
		{/if}
	</Container>
{:else}
	{#if (device.category === 'module' && device.mode === 'inputs_reader') || device.category === 'inputs_reader'}
		<ButtonTypeInputsReader bind:typeValue />
	{:else}
		<ButtonType bind:typeValue {options} {_options} />
		<hr class="mb-m mt-s" />
		<ButtonModeOptions
			{typeValue}
			bind:modeValue
			{buttonModeOptions}
			_modeValue={modeValue}
			bind:reactiveStateValue
		/>
		<InitialStateOptions
			{typeValue}
			bind:initialStateValue
			{mandatoryUpdate}
			{initialStateOptions}
			{modeValue}
		/>
	{/if}
{/if}

{#if $showNewLayout}
	<PhysicalInputContainer visible={showPhysicalInput}>
		{#if showEnableInput}
			<div class="mb-m">
				<EnableInputG2
					{device}
					deferSave
					bind:value={enableValue}
					title={$format('sub_settings_title_enable_input', { default: 'Enable input' })}
				/>
			</div>
		{/if}
		{#if showLockInputs}
			<div class="mb-m">
				<LockInputsG2
					{device}
					deferSave
					bind:value={lockValue}
					title={$format('sub_settings_title_lock_inputs', { default: 'Lock inputs' })}
				/>
			</div>
		{/if}
		{#if showInvertSwitch}
			<div class="mb-m">
				<InvertSwitchG2
					{device}
					deferSave
					bind:value={invertValue}
					title={capFirstChar(
						$format('sub_settings_title_invert_switch', { default: 'Invert switch' }),
					)}
				/>
			</div>
		{/if}
		{#if hasInputStateSetting}
			<AdvancedSettingBlock collapsed={!showInputState}>
				<InputState
					{device}
					deferSave
					bind:value={inputStateValue}
					title={capFirstChar($format('sub_settings_title_input_state', { default: 'Input state' }))}
				/>
			</AdvancedSettingBlock>
		{/if}
	</PhysicalInputContainer>
{/if}

{#if !$showNewLayout}
	<Button class="mt-s" {loading} size="m" width="full" on:click={sentRequests}>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
